/**
 * 🌐 Network Quality Detection Service
 * 
 * Service avancé de détection de la qualité de connexion internet
 * inspiré de YouTube pour adapter le chargement des composants
 */

export type ConnectionQuality = 'excellent' | 'good' | 'moderate' | 'poor' | 'offline';

export interface NetworkMetrics {
  quality: ConnectionQuality;
  effectiveType: '4g' | '3g' | '2g' | 'slow-2g' | 'unknown';
  downlink: number; // Mbps
  rtt: number; // Round Trip Time en ms
  saveData: boolean;
  isOnline: boolean;
  lastUpdated: number;
}

/**
 * Network Information API (non standardisée, absente des typings DOM de TypeScript).
 * Seuls les membres lus par ce service sont décrits.
 */
interface NetworkInformationLike extends EventTarget {
  effectiveType?: NetworkMetrics['effectiveType'];
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

type NavigatorWithConnection = Navigator & {
  connection?: NetworkInformationLike;
  mozConnection?: NetworkInformationLike;
  webkitConnection?: NetworkInformationLike;
};

export interface NetworkQualityConfig {
  // Seuils de qualité (en Mbps)
  thresholds: {
    excellent: number;
    good: number;
    moderate: number;
    poor: number;
  };
  // Fréquence de vérification (en ms)
  checkInterval: number;
  // Taille d'échantillon pour la moyenne
  sampleSize: number;
}

const DEFAULT_CONFIG: NetworkQualityConfig = {
  thresholds: {
    excellent: 10,
    good: 5,
    moderate: 2,
    poor: 0.5,
  },
  checkInterval: 10000, // 10 secondes
  sampleSize: 5,
};

type NetworkChangeCallback = (metrics: NetworkMetrics) => void;

class NetworkQualityService {
  private config: NetworkQualityConfig;
  private metrics: NetworkMetrics;
  private listeners: Set<NetworkChangeCallback>;
  private speedSamples: number[];
  private checkIntervalId: number | null;
  private lastSpeedTest: number;

  constructor(config: Partial<NetworkQualityConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.listeners = new Set();
    this.speedSamples = [];
    this.checkIntervalId = null;
    this.lastSpeedTest = 0;

    this.metrics = {
      quality: 'good',
      effectiveType: 'unknown',
      downlink: 10,
      rtt: 50,
      saveData: false,
      isOnline: navigator.onLine,
      lastUpdated: Date.now(),
    };

    this.initialize();
  }

  private initialize(): void {
    // Écouter les changements de connexion
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);

    // Utiliser l'API Network Information si disponible
    const connection = this.getNetworkInfo();
    if (connection) {
      connection.addEventListener('change', this.handleConnectionChange);
      this.updateFromNetworkInfo();
    }

    // Effectuer une vérification initiale
    this.performSpeedTest();

    // Démarrer les vérifications périodiques
    this.startPeriodicChecks();
  }

  private getNetworkInfo(): NetworkInformationLike | undefined {
    const nav = navigator as NavigatorWithConnection;
    return (
      nav.connection ||
      nav.mozConnection ||
      nav.webkitConnection
    );
  }

  private handleOnline = (): void => {
    this.metrics.isOnline = true;
    this.updateQuality();
    this.notifyListeners();
    // Test de vitesse après reconnexion
    setTimeout(() => this.performSpeedTest(), 1000);
  };

  private handleOffline = (): void => {
    this.metrics = {
      ...this.metrics,
      quality: 'offline',
      isOnline: false,
      lastUpdated: Date.now(),
    };
    this.notifyListeners();
  };

  private handleConnectionChange = (): void => {
    this.updateFromNetworkInfo();
    this.performSpeedTest();
  };

  private updateFromNetworkInfo(): void {
    const connection = this.getNetworkInfo();
    if (!connection) return;

    this.metrics = {
      ...this.metrics,
      effectiveType: connection.effectiveType || 'unknown',
      downlink: connection.downlink || 10,
      rtt: connection.rtt || 50,
      saveData: connection.saveData || false,
      lastUpdated: Date.now(),
    };

    this.updateQuality();
  }

  private updateQuality(): void {
    if (!this.metrics.isOnline) {
      this.metrics.quality = 'offline';
      return;
    }

    const { thresholds } = this.config;
    const { downlink, rtt, saveData, effectiveType } = this.metrics;

    // Ajuster basé sur l'effectiveType
    let qualityScore = downlink;
    
    if (effectiveType === 'slow-2g' || effectiveType === '2g') {
      qualityScore = Math.min(qualityScore, 0.3);
    } else if (effectiveType === '3g') {
      qualityScore = Math.min(qualityScore, 2);
    }

    // Pénalité pour RTT élevé
    if (rtt > 500) {
      qualityScore *= 0.5;
    } else if (rtt > 200) {
      qualityScore *= 0.75;
    }

    // Mode économie de données
    if (saveData) {
      this.metrics.quality = 'poor';
      return;
    }

    // Déterminer la qualité
    if (qualityScore >= thresholds.excellent) {
      this.metrics.quality = 'excellent';
    } else if (qualityScore >= thresholds.good) {
      this.metrics.quality = 'good';
    } else if (qualityScore >= thresholds.moderate) {
      this.metrics.quality = 'moderate';
    } else {
      this.metrics.quality = 'poor';
    }
  }

  /**
   * Test de vitesse léger utilisant une requête timestamp
   */
  async performSpeedTest(): Promise<number> {
    // Éviter les tests trop fréquents
    if (Date.now() - this.lastSpeedTest < 5000) {
      return this.metrics.downlink;
    }

    this.lastSpeedTest = Date.now();

    if (!this.metrics.isOnline) {
      return 0;
    }

    // ✨ Utiliser uniquement l'API Network Information du navigateur
    // Pas de requêtes HTTP pour éviter les erreurs 404
    const connection = this.getNetworkInfo();
    
    if (connection && typeof connection.downlink === 'number') {
      const estimatedSpeed = connection.downlink;
      
      // Ajouter à l'échantillonnage
      this.speedSamples.push(estimatedSpeed);
      if (this.speedSamples.length > this.config.sampleSize) {
        this.speedSamples.shift();
      }

      // Calculer la moyenne
      const avgSpeed =
        this.speedSamples.reduce((a, b) => a + b, 0) / this.speedSamples.length;

      this.metrics.downlink = avgSpeed;
      this.metrics.rtt = connection.rtt || 50;
      this.metrics.lastUpdated = Date.now();
      
      this.updateQuality();
      this.notifyListeners();

      return avgSpeed;
    }
    
    // Fallback: si l'API Network Information n'est pas disponible,
    // garder les valeurs par défaut (connexion supposée bonne si online)
    if (this.metrics.isOnline && this.speedSamples.length === 0) {
      this.speedSamples.push(10); // Valeur par défaut optimiste
    }
    
    return this.metrics.downlink;
  }

  private startPeriodicChecks(): void {
    if (this.checkIntervalId) return;

    this.checkIntervalId = window.setInterval(() => {
      this.performSpeedTest();
    }, this.config.checkInterval);
  }

  private stopPeriodicChecks(): void {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
  }

  private notifyListeners(): void {
    this.listeners.forEach((callback) => {
      try {
        callback({ ...this.metrics });
      } catch (error) {
        console.error('Network quality listener error:', error);
      }
    });
  }

  // API Publique

  getMetrics(): NetworkMetrics {
    return { ...this.metrics };
  }

  getQuality(): ConnectionQuality {
    return this.metrics.quality;
  }

  isSlowConnection(): boolean {
    return ['poor', 'moderate', 'offline'].includes(this.metrics.quality);
  }

  shouldReduceData(): boolean {
    return this.metrics.saveData || this.metrics.quality === 'poor';
  }

  subscribe(callback: NetworkChangeCallback): () => void {
    this.listeners.add(callback);
    // Envoyer les métriques actuelles immédiatement
    callback({ ...this.metrics });
    
    return () => {
      this.listeners.delete(callback);
    };
  }

  /**
   * Configuration de qualité recommandée pour le chargement
   */
  getLoadingConfig(): {
    imageQuality: 'high' | 'medium' | 'low';
    preloadCount: number;
    animationsEnabled: boolean;
    lazyLoadOffset: string;
    chunkSize: number;
    maxConcurrentLoads: number;
    useSkeletons: boolean;
    videoAutoplay: boolean;
  } {
    switch (this.metrics.quality) {
      case 'excellent':
        return {
          imageQuality: 'high',
          preloadCount: 8,
          animationsEnabled: true,
          lazyLoadOffset: '200px',
          chunkSize: 20,
          maxConcurrentLoads: 6,
          useSkeletons: false,
          videoAutoplay: true,
        };
      case 'good':
        return {
          imageQuality: 'high',
          preloadCount: 5,
          animationsEnabled: true,
          lazyLoadOffset: '150px',
          chunkSize: 15,
          maxConcurrentLoads: 4,
          useSkeletons: true,
          videoAutoplay: true,
        };
      case 'moderate':
        return {
          imageQuality: 'medium',
          preloadCount: 3,
          animationsEnabled: true,
          lazyLoadOffset: '100px',
          chunkSize: 10,
          maxConcurrentLoads: 3,
          useSkeletons: true,
          videoAutoplay: false,
        };
      case 'poor':
        return {
          imageQuality: 'low',
          preloadCount: 1,
          animationsEnabled: false,
          lazyLoadOffset: '50px',
          chunkSize: 5,
          maxConcurrentLoads: 2,
          useSkeletons: true,
          videoAutoplay: false,
        };
      case 'offline':
      default:
        return {
          imageQuality: 'low',
          preloadCount: 0,
          animationsEnabled: false,
          lazyLoadOffset: '0px',
          chunkSize: 5,
          maxConcurrentLoads: 1,
          useSkeletons: true,
          videoAutoplay: false,
        };
    }
  }

  destroy(): void {
    this.stopPeriodicChecks();
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);

    const connection = this.getNetworkInfo();
    if (connection) {
      connection.removeEventListener('change', this.handleConnectionChange);
    }

    this.listeners.clear();
  }
}

// Instance singleton
let instance: NetworkQualityService | null = null;

export function getNetworkQualityService(
  config?: Partial<NetworkQualityConfig>
): NetworkQualityService {
  if (!instance) {
    instance = new NetworkQualityService(config);
  }
  return instance;
}

export function destroyNetworkQualityService(): void {
  if (instance) {
    instance.destroy();
    instance = null;
  }
}

export default NetworkQualityService;
